import { useCallback, useMemo, useState } from 'react'
import { Button } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import type { Plugin } from 'prettier'
import { numberLocale, useLocale, useT } from '@/shared/i18n'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { CodeWorkspace } from '@/shared/ui/code-workspace'

const UTILITY = 'css-formatter'

type CssResult = { ok: true; result: string } | { ok: false; error: string }

interface ParserLocation {
  start?: {
    line?: number
    column?: number
  }
  line?: number
  column?: number
}

interface CssIssue extends Error {
  loc?: ParserLocation
  line?: number
  column?: number
}

async function loadCssFormatter() {
  const [prettier, postcssPlugin] = await Promise.all([
    import('prettier/standalone'),
    import('prettier/plugins/postcss'),
  ])

  return {
    prettier,
    plugins: [postcssPlugin] as Plugin[],
  }
}

function formatCssIssue(error: unknown): string {
  if (!(error instanceof Error)) return String(error)

  const issue = error as CssIssue
  const line = issue.loc?.start?.line ?? issue.loc?.line ?? issue.line
  const column = issue.loc?.start?.column ?? issue.loc?.column ?? issue.column
  if (line && column) {
    return `line ${line}, column ${column}: ${error.message}`
  }

  return error.message
}

async function formatCss(input: string): Promise<CssResult> {
  if (!input.trim()) return { ok: false, error: 'empty' }

  try {
    const { prettier, plugins } = await loadCssFormatter()
    const result = await prettier.format(input, {
      parser: 'css',
      plugins,
      printWidth: 100,
      tabWidth: 2,
    })

    return { ok: true, result: result.trimEnd() }
  } catch (error) {
    return { ok: false, error: formatCssIssue(error) }
  }
}

function stripCssComments(input: string): string {
  let output = ''
  let index = 0
  let quote: '"' | "'" | null = null

  while (index < input.length) {
    const char = input[index] ?? ''
    const next = input[index + 1]

    if (quote) {
      output += char
      if (char === '\\' && next) {
        output += next
        index += 2
        continue
      }
      if (char === quote) quote = null
      index += 1
      continue
    }

    if (char === '"' || char === "'") {
      quote = char
      output += char
      index += 1
      continue
    }

    if (char === '/' && next === '*') {
      const end = input.indexOf('*/', index + 2)
      if (input[index + 2] === '!') {
        const preservedEnd = end === -1 ? input.length : end + 2
        output += input.slice(index, preservedEnd)
      }
      index = end === -1 ? input.length : end + 2
      continue
    }

    output += char
    index += 1
  }

  return output
}

function isCssWhitespace(char: string): boolean {
  return /\s/.test(char)
}

function isTightCssPunctuation(char: string): boolean {
  return /[{}:;,>~]/.test(char)
}

function lastChar(input: string): string {
  return input[input.length - 1] ?? ''
}

function minifyFormattedCss(input: string): string {
  const withoutComments = stripCssComments(input)
  let output = ''
  let quote: '"' | "'" | null = null
  let pendingSpace = false

  for (let index = 0; index < withoutComments.length; index += 1) {
    const char = withoutComments[index] ?? ''
    const next = withoutComments[index + 1]

    if (quote) {
      output += char
      if (char === '\\' && next) {
        output += next
        index += 1
        continue
      }
      if (char === quote) quote = null
      continue
    }

    if (char === '"' || char === "'") {
      if (pendingSpace && output && !isTightCssPunctuation(lastChar(output))) {
        output += ' '
      }
      pendingSpace = false
      quote = char
      output += char
      continue
    }

    if (isCssWhitespace(char)) {
      pendingSpace = true
      continue
    }

    if (isTightCssPunctuation(char)) {
      output = output.trimEnd()
      if (char === '}' && output.endsWith(';')) {
        output = output.slice(0, -1)
      }
      output += char
      pendingSpace = false
      continue
    }

    if (pendingSpace && output && !isTightCssPunctuation(lastChar(output))) {
      output += ' '
    }
    pendingSpace = false
    output += char
  }

  return output.trim()
}

async function minifyCss(input: string): Promise<CssResult> {
  const formatted = await formatCss(input)
  if (!formatted.ok) return formatted

  return { ok: true, result: minifyFormattedCss(formatted.result) }
}

export function CssFormatterPage() {
  const t = useT()
  const { locale } = useLocale()
  const localeCode = numberLocale(locale)
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [copied, setCopied] = useState(false)

  const inputStats = useMemo(() => {
    const chars = input.length
    const lines = input ? input.split('\n').length : 0
    return { chars, lines }
  }, [input])

  const outputStats = useMemo(() => {
    const chars = output.length
    const lines = output ? output.split('\n').length : 0
    return { chars, lines }
  }, [output])

  const processCss = useCallback(async (action: 'format' | 'minify') => {
    if (!input.trim()) {
      setOutput('')
      setError(null)
      setStatusMessage(null)
      return
    }

    const start = performance.now()
    setIsProcessing(true)
    setError(null)
    setStatusMessage(null)
    toolInvoke(UTILITY, { action })

    const result = action === 'format'
      ? await formatCss(input)
      : await minifyCss(input)

    if (result.ok) {
      setOutput(result.result)
      setStatusMessage(
        action === 'format' ? t.cssFormatter.formatted : t.cssFormatter.minified,
      )
      toolSuccess(UTILITY, Math.round(performance.now() - start), { action })
    } else {
      setOutput('')
      setError(t.cssFormatter.errors.invalidCss.replace('{error}', result.error))
      toolError(UTILITY, 'invalid-css', { action })
    }

    setIsProcessing(false)
  }, [input, t])

  const handleCopy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }, [output])

  const handleClear = useCallback(() => {
    setInput('')
    setOutput('')
    setError(null)
    setStatusMessage(null)
  }, [])

  return (
    <ToolPageShell toolKey="cssFormatter" width="wide">
      <CodeWorkspace
        error={error}
        notice={statusMessage}
        controls={(
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={() => void processCss('format')}
              disabled={!input.trim() || isProcessing}
            >
              {t.cssFormatter.format}
            </Button>
            <Button
              variant="secondary"
              onClick={() => void processCss('minify')}
              disabled={!input.trim() || isProcessing}
            >
              {t.cssFormatter.minify}
            </Button>
          </div>
        )}
        input={{
          title: t.cssFormatter.input,
          value: input,
          onChange: (value) => {
            setInput(value)
            setError(null)
            setStatusMessage(null)
          },
          placeholder: t.cssFormatter.inputPlaceholder,
          ariaLabel: t.cssFormatter.input,
          clear: { label: t.cssFormatter.clear, onClick: handleClear },
          stats: [
            {
              label: t.cssFormatter.characters,
              value: inputStats.chars.toLocaleString(localeCode),
            },
            {
              label: t.cssFormatter.lines,
              value: inputStats.lines.toLocaleString(localeCode),
            },
          ],
        }}
        output={{
          title: t.cssFormatter.output,
          value: output,
          readOnly: true,
          placeholder: t.cssFormatter.outputPlaceholder,
          ariaLabel: t.cssFormatter.output,
          actions: (
            <Button variant="outline" size="sm" onClick={handleCopy}>
              {copied ? t.cssFormatter.copied : t.cssFormatter.copy}
            </Button>
          ),
          stats: output && !error
            ? [
              {
                label: t.cssFormatter.characters,
                value: outputStats.chars.toLocaleString(localeCode),
              },
              {
                label: t.cssFormatter.lines,
                value: outputStats.lines.toLocaleString(localeCode),
              },
            ]
            : undefined,
        }}
      />

      <section className="mt-10 space-y-5 border-t pt-8">
        <div>
          <h2 className="text-xl font-semibold tracking-normal">
            {t.cssFormatter.howTitle}
          </h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.cssFormatter.how}
          </p>
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-normal">
            {t.cssFormatter.whenTitle}
          </h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.cssFormatter.when}
          </p>
        </div>
      </section>
    </ToolPageShell>
  )
}
